import React, { useState } from 'react';
import { LogOut, X, ChevronDown } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import LanguageToggle from './LanguageToggle';
import { NAV_ITEMS } from '../navItems';

/**
 * The desktop sidebar's content, as a phone drawer.
 *
 * It carries the branch switcher as well as the nav list: a dentist working
 * across branches has no other way to switch on a phone, because the sidebar
 * that normally holds the control is off screen.
 */
const NavDrawer = ({ open, user, currentPage, onNavigate, onLogout, onClose, onBranchSwitch }) => {
  const { print_lang } = useLanguage();
  const [branchOpen, setBranchOpen] = useState(false);

  if (!open) return null;

  const branches = user?.branches || [];
  const current = branches.find((b) => b.branch_id === user?.branch_id);
  const canSwitch = branches.length > 1;

  const choose = (id) => {
    onNavigate(id);
    onClose();
  };

  return (
    <>
      <div className="nav-drawer-scrim" onClick={onClose} />
      <aside className="nav-drawer" role="dialog" aria-label={print_lang('menu', 'Menu')}>
        <div className="nav-drawer-header">
          <span className="nav-drawer-title">{print_lang('dentist_portal', 'Dentist Portal')}</span>
          <button
            type="button"
            className="nav-drawer-close"
            onClick={onClose}
            aria-label={print_lang('close', 'Close')}
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        <div className="nav-drawer-branch">
          <button
            type="button"
            className={canSwitch ? 'nav-drawer-branch-toggle' : 'nav-drawer-branch-static'}
            onClick={canSwitch ? () => setBranchOpen((o) => !o) : undefined}
            disabled={!canSwitch}
          >
            <span className="truncate">{current?.branch_name || `Branch ${user?.branch_id}`}</span>
            {canSwitch && <ChevronDown className="w-4 h-4" aria-hidden="true" />}
          </button>
          {branchOpen && canSwitch && (
            <ul className="nav-drawer-branch-list">
              {branches.map((b) => (
                <li key={b.branch_id}>
                  <button
                    type="button"
                    className={`nav-drawer-branch-option ${b.branch_id === user?.branch_id ? 'active' : ''}`}
                    onClick={() => { setBranchOpen(false); onBranchSwitch(b.branch_id); }}
                  >
                    {b.branch_name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <nav>
          <ul className="nav-drawer-list">
            {NAV_ITEMS.map(({ id, labelKey, labelFallback, Icon }) => (
              <li key={id}>
                <button
                  type="button"
                  className={`nav-drawer-item ${currentPage === id ? 'active' : ''}`}
                  onClick={() => choose(id)}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                  {print_lang(labelKey, labelFallback)}
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                className="nav-drawer-item nav-drawer-item-danger"
                onClick={() => { onLogout(); onClose(); }}
              >
                <LogOut className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                {print_lang('logout', 'Logout')}
              </button>
            </li>
          </ul>
        </nav>

        <div className="nav-drawer-footer">
          <div className="nav-drawer-user">{user?.name}</div>
          <LanguageToggle />
        </div>
      </aside>
    </>
  );
};

export default NavDrawer;
